import { Box, InputAdornment, TextField } from "@material-ui/core";
import { Search, LocalOfferOutlined } from "@material-ui/icons";
import { Pagination } from "@material-ui/lab";
import Breadcrumbs from "components/Breadcrumbs";
import Link from "components/Link";
import NoDataAvailable from "components/NoDataAvailable";
import SEO from "components/SEO";
import SessionContext from "components/session/SessionContext";
import Text from "components/Text";
import { pagePadding } from "data/commonClasses";
import {
  fetchFromAPI,
  fetchPost,
  getTranslationContent,
  isArray,
} from "functions";
import Image from "next/image";
import { useContext, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";

// Utility function to strip HTML tags
const stripHtmlTags = (str) => {
  if (!str) return "";
  return str.replace(/<\/?[^>]+(>|$)/g, "");
};

const ITEMS_PER_PAGE = 10;

export default function Benefits(props) {
  const { t } = useTranslation();
  let {
    session: { lang },
  } = useContext(SessionContext);
  let isAr = lang === "ar";
  const [benefits, setBenefits] = useState(props.benefits || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(props.totalCount || 0);
  const [initialTotalCount] = useState(props.totalCount || 0);
  const [showSearch, setShowSearch] = useState((props.totalCount || 0) >= 10);
  const [totalPages, setTotalPages] = useState(
    Math.ceil((props.totalCount || 0) / ITEMS_PER_PAGE)
  );
  const [isLoading, setIsLoading] = useState(false);

  // Handle search and page changes server-side
  const fetchBenefits = async (page, searchText) => {
    setIsLoading(true);
    try {
      const reqBody = {
        page,
        limit: ITEMS_PER_PAGE,
        stripHtml: true,
        lang,
        searchText: searchText?.trim(),
      };

      const response = await fetchFromAPI({ api: "benefits", reqBody });
      const fetchedBenefits = response?.response || [];
      const count = fetchedBenefits.length || 0;

      setBenefits(fetchedBenefits);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Error fetching benefits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchBenefits(currentPage);
    }
  }, [currentPage]);

  // Handle search
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setCurrentPage(1);
    fetchBenefits(1, searchValue);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  var breadcrumbs = [
    { text: t("home"), href: "/" },
    { text: t("discountsAndBenefits") },
  ];

  return (
    <div className={twMerge("flex flex-col w-full min-h-screen", pagePadding)}>
      <SEO
        title={t("discountsAndBenefits")}
        description={t("benefitsDescription")}
      />
      <Breadcrumbs items={breadcrumbs} />
      <Text
        text={t("discountsAndBenefits")}
        bold
        sectionTitle
        className="mt-5 mb-8"
      />
      <div className="w-full min-h-[300px] bg-[#F2F3F2] flex flex-col p-8 rounded-xl sm:p-4">
        <div className="flex flex-col w-full mx-auto">
          {(showSearch || searchTerm) && (
            <div className="relative w-full max-w-[600px] mx-auto my-4">
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t("searchIn") + " " + t("discountsAndBenefits")}
                value={searchTerm}
                onChange={handleSearch}
                className="bg-white rounded-lg transition-all duration-300 focus-within:ring-2 focus-within:ring-[#F5B750]"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-gray-500 focus-within:text-[#F5B750]" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
          {!isArray(benefits) || benefits.length === 0 ? (
            <NoDataAvailable />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-8 py-4">
                {benefits.map((benefit) => (
                  <Link
                    href="/benefits/details/[slug]"
                    as={`/benefits/details/${benefit._id}`}
                    passHref={true}
                    key={benefit._id}
                  >
                    <div className="relative bg-white rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col h-full cursor-pointer shadow-md">
                      <div className="relative w-full h-[200px] bg-[#F2F3F2] flex-shrink-0">
                        <Image
                          src={benefit.image || "/placeholder.png"}
                          alt={
                            getTranslationContent(benefit, "title", lang) ||
                            getTranslationContent(benefit, "companyName", lang)
                          }
                          layout="fill"
                          objectFit="cover"
                          priority={true}
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-1 h-full">
                        <h3 className="text-xl font-semibold text-[#344450] mb-1 leading-[1.4] overflow-hidden line-clamp-2 h-[2em]">
                          {benefit.title
                            ? getTranslationContent(benefit, "title", lang)
                            : getTranslationContent(
                                benefit,
                                "companyName",
                                lang
                              )}
                        </h3>
                        {benefit.discount && (
                          <div className="flex items-center bg-[#f9f2e6] text-[#F5B750] font-semibold text-[0.95rem] rounded px-3 py-1 mb-2 w-fit">
                            <LocalOfferOutlined className="text-[1.1rem] mr-2" />
                            {benefit.discount} {t("Discount")}
                          </div>
                        )}
                        <div className="flex-1 flex flex-col">
                          <div  dangerouslySetInnerHTML={{ __html: benefit.description }} className="text-gray-600 text-sm leading-[1.6] overflow-hidden line-clamp-3 mt-[0.1rem] h-[4.5em]">
                            {/* {stripHtmlTags(
                              getTranslationContent(
                                benefit,
                                "description",
                                lang
                              ) || ""
                            )?.substring(0, 100)} */}
                            {/* ... */}
                          </div>
                        </div>
                        <div className="flex items-center justify-end border-t border-gray-200 mt-3 pt-3 flex-shrink-0">
                          {benefit.link && (
                            <a
                              href={benefit.link}
                              className="text-[#F5B750] text-sm no-underline flex items-center py-2 hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("visitWebsite")}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex sm:justify-end justify-center my-12">
                  <Pagination
                    page={currentPage}
                    count={totalPages}
                    onChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export async function getServerSideProps({ params }) {
  let { lang } = params;
  let benefits = [];
  let reqBody = {
    page: 1,
    limit: ITEMS_PER_PAGE,
    stripHtml: true,
    lang,
  };
  let response = await fetchPost({ apiName: "benefits", reqBody });
  benefits = response?.response || [];

  return {
    props: {
      benefits,
      totalCount: benefits.length || 0,
    },
  };
}
